import { Table } from '@tanstack/react-table';
import { Loader2Icon } from 'lucide-react';

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  isFetching?: boolean;
  pageRange?: number;
  pageSizeOptions?: number[];
}

// Simplified page range algorithm
const generatePageRange = (
  currentPage: number,
  totalPages: number,
  range: number = 2
) => {
  // Use a Set to avoid duplicates
  const pageSet = new Set<number>();

  // Always add first page
  pageSet.add(1);

  if (totalPages === 1) {
    return [1];
  }

  // Add pages around current page based on range
  for (let i = currentPage - range; i <= currentPage + range; i++) {
    if (i > 1 && i < totalPages) {
      pageSet.add(i);
    }
  }

  // Always add last page if we have more than 1 page
  if (totalPages > 1) {
    pageSet.add(totalPages);
  }

  // Convert to array and sort
  const pages = Array.from(pageSet).sort((a, b) => a - b);

  // Add ellipsis where needed
  const pagesWithEllipsis: (number | string)[] = [];

  let prevPage: number | null = null;
  for (const page of pages) {
    if (prevPage !== null && page - prevPage > 1) {
      pagesWithEllipsis.push('...');
    }
    pagesWithEllipsis.push(page);
    prevPage = page;
  }

  return pagesWithEllipsis;
};

export function DataTablePagination<TData>({
  table,
  isFetching,
  pageRange = 2,
  pageSizeOptions = [10, 20, 50, 100, 200, 300, 400, 500],
}: DataTablePaginationProps<TData>) {
  const currentPage = table.getState().pagination.pageIndex + 1;
  const totalPages = table.getPageCount();

  return (
    <div className="flex items-center justify-between px-2 bg-white py-2">
      <div className="flex items-center space-x-6">
        <p className="text-sm font-medium">
          Page {currentPage} of {totalPages}
        </p>
        <Select
          value={`${table.getState().pagination.pageSize}`}
          onValueChange={(value) => {
            table.setPageSize(Number(value));
          }}
        >
          <SelectTrigger className="h-8 w-[80px]">
            <SelectValue placeholder={table.getState().pagination.pageSize} />
          </SelectTrigger>
          <SelectContent side="top">
            {pageSizeOptions.map((pageSize) => (
              <SelectItem key={pageSize} value={`${pageSize}`}>
                {pageSize}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Pagination className="flex-1 flex justify-end">
        <PaginationContent>
          {isFetching ? <Loader2Icon className="w-4 h-4 animate-spin" /> : null}
          <PaginationItem>
            {!table.getCanPreviousPage() ? (
              <PaginationPrevious
                href="#"
                onClick={(e) => e.preventDefault()}
                className="pointer-events-none opacity-50"
              />
            ) : (
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  table.previousPage();
                }}
              />
            )}
          </PaginationItem>
          {generatePageRange(currentPage, totalPages, pageRange).map(
            (pageNumber, idx) =>
              pageNumber === '...' ? (
                <PaginationItem key={`ellipsis-${idx}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={pageNumber}>
                  <PaginationLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      table.setPageIndex(Number(pageNumber) - 1);
                    }}
                    isActive={currentPage === Number(pageNumber)}
                  >
                    {pageNumber}
                  </PaginationLink>
                </PaginationItem>
              )
          )}
          <PaginationItem>
            {!table.getCanNextPage() ? (
              <PaginationNext
                href="#"
                onClick={(e) => e.preventDefault()}
                className="pointer-events-none opacity-50"
              />
            ) : (
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  table.nextPage();
                }}
              />
            )}
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
